import { useState } from 'react';
import {
  Banknote,
  CreditCard,
  Smartphone,
  Undo2,
  FileSpreadsheet,
  FileText,
  Tag,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Pagination } from '@/components/ui/Pagination';
import { EstornarVendaModal, type VendaParaEstorno } from './EstornarVendaModal';
import { exportarVendasExcel, exportarVendasPdf } from '@/lib/relatorios/exportarVendas';

const ICONE_PAGAMENTO = {
  DINHEIRO: Banknote,
  PIX: Smartphone,
  CARTAO_CREDITO: CreditCard,
  CARTAO_DEBITO: CreditCard,
} as const;

const ROTULO_PAGAMENTO = {
  DINHEIRO: 'Dinheiro',
  PIX: 'PIX',
  CARTAO_CREDITO: 'Crédito',
  CARTAO_DEBITO: 'Débito',
} as const;

const moeda = (valor: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);

const diaCurto = (iso: string) => {
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
};

interface TabVendasProps {
  dataInicio: string;
  dataFim: string;
}

/**
 * Barra de magnitude dentro da tabela.
 *
 * Substitui a pizza de propósito: com poucas fatias a tabela dá o número exato
 * e a proporção ao mesmo tempo, sem depender de comparar ângulos.
 */
function BarraProporcao({ valor, maximo }: { valor: number; maximo: number }) {
  const largura = maximo > 0 ? Math.max((valor / maximo) * 100, 2) : 0;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
      <div className="bg-primary-500 h-full rounded-full" style={{ width: `${largura}%` }} />
    </div>
  );
}

export function TabVendas({ dataInicio, dataFim }: TabVendasProps) {
  const { hasPermission } = useAuth();
  const podeEstornar = hasPermission('ADMIN') || hasPermission('FINANCEIRO');

  const [page, setPage] = useState(1);
  const [incluirCanceladas, setIncluirCanceladas] = useState(false);
  const [estornando, setEstornando] = useState<VendaParaEstorno | null>(null);

  const periodo = {
    dataInicio: dataInicio ? new Date(dataInicio) : undefined,
    dataFim: dataFim ? new Date(dataFim) : undefined,
  };

  const { data: relatorio, isLoading } = trpc.relatorios.vendas.useQuery({
    ...periodo,
    limiteRanking: 20,
  });

  const { data: lista } = trpc.relatorios.listVendas.useQuery({
    ...periodo,
    page,
    limit: 10,
    incluirCanceladas,
  });

  if (isLoading || !relatorio) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  const { resumo, porDia, porFormaPagamento, porCategoria, rankingProdutos } = relatorio;
  const maiorCategoria = Math.max(...porCategoria.map((c) => c.total), 0);
  const maiorProduto = Math.max(...rankingProdutos.map((p) => p.total), 0);
  const maiorPagamento = Math.max(...porFormaPagamento.map((f) => f.total), 0);

  const semVendas = resumo.quantidade === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={semVendas}
          onClick={() => exportarVendasExcel(relatorio, { dataInicio, dataFim })}
        >
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Excel
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={semVendas}
          onClick={() => exportarVendasPdf(relatorio, { dataInicio, dataFim })}
        >
          <FileText className="mr-2 h-4 w-4" />
          PDF
        </Button>
      </div>

      {/* Números do período */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="border-primary-200 bg-primary-50 rounded-xl border p-5">
          <p className="text-primary-700 text-sm">Faturamento</p>
          <p className="text-primary-700 mt-1 text-3xl font-bold">{moeda(resumo.total)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
          <p className="text-sm text-gray-500">Vendas</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{resumo.quantidade}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
          <p className="text-sm text-gray-500">Ticket médio</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{moeda(resumo.ticketMedio)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
          <p className="text-sm text-gray-500">Estornadas</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{resumo.estornadas}</p>
          {resumo.estornadas > 0 && (
            <p className="mt-1 text-xs text-gray-500">{moeda(resumo.totalEstornado)} devolvidos</p>
          )}
        </div>
      </div>

      {semVendas ? (
        <Card>
          <CardContent className="py-16 text-center text-gray-400">
            Nenhuma venda registrada no período selecionado.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Único gráfico: evolução no tempo é o que prosa não conta bem. */}
          <Card>
            <CardHeader>
              <CardTitle>Faturamento por dia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={porDia} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="dia"
                      tickFormatter={diaCurto}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                    />
                    <YAxis
                      tickFormatter={(v: number) =>
                        v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`
                      }
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      width={44}
                    />
                    <Tooltip
                      cursor={{ fill: '#f8fafc' }}
                      labelFormatter={(iso) =>
                        // Meio-dia evita o dia virar para trás na conversão de fuso.
                        new Date(`${String(iso)}T12:00:00`).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                        })
                      }
                      formatter={(valor, _nome, item) => [
                        `${moeda(Number(valor))} · ${(item?.payload as { quantidade?: number })?.quantidade ?? 0} vendas`,
                        'Faturamento',
                      ]}
                    />
                    <Bar dataKey="total" fill="#0284c7" radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Por forma de pagamento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {porFormaPagamento.map((forma) => {
                  const Icone = ICONE_PAGAMENTO[forma.formaPagamento];
                  return (
                    <div key={forma.formaPagamento} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-gray-700">
                          <Icone className="h-4 w-4 text-gray-400" />
                          {ROTULO_PAGAMENTO[forma.formaPagamento]}
                          <span className="text-gray-400">({forma.quantidade})</span>
                        </span>
                        <span className="font-semibold text-gray-900">{moeda(forma.total)}</span>
                      </div>
                      <BarraProporcao valor={forma.total} maximo={maiorPagamento} />
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Por categoria</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {porCategoria.map((categoria) => (
                  <div key={categoria.categoria} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-gray-700">
                        <Tag className="h-4 w-4 text-gray-400" />
                        {categoria.categoria}
                        <span className="text-gray-400">({categoria.unidades} un)</span>
                      </span>
                      <span className="font-semibold text-gray-900">{moeda(categoria.total)}</span>
                    </div>
                    <BarraProporcao valor={categoria.total} maximo={maiorCategoria} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Produtos mais vendidos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {rankingProdutos.map((produto, indice) => (
                  <div key={produto.produtoId} className="flex items-center gap-3">
                    <span className="w-6 text-right text-sm font-medium text-gray-400">
                      {indice + 1}
                    </span>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="truncate text-gray-800">
                          {produto.nome}
                          <span className="ml-2 font-mono text-xs text-gray-400">
                            {produto.codigo}
                          </span>
                        </span>
                        <span className="whitespace-nowrap font-semibold text-gray-900">
                          {moeda(produto.total)}
                          <span className="ml-2 text-xs font-normal text-gray-400">
                            {produto.unidades} un
                          </span>
                        </span>
                      </div>
                      <BarraProporcao valor={produto.total} maximo={maiorProduto} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Vendas do período</CardTitle>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={incluirCanceladas}
                onChange={(e) => {
                  setIncluirCanceladas(e.target.checked);
                  setPage(1);
                }}
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              Mostrar estornadas
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {!lista || lista.items.length === 0 ? (
            <p className="py-8 text-center text-gray-400">Nenhuma venda no período.</p>
          ) : (
            <>
              <div className="space-y-3">
                {lista.items.map((venda) => {
                  const Icone = ICONE_PAGAMENTO[venda.formaPagamento];
                  return (
                    <div
                      key={venda.id}
                      className={`rounded-lg border p-4 ${
                        venda.cancelada ? 'border-red-200 bg-red-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <Icone className="h-4 w-4 text-gray-400" />
                          <span>{new Date(venda.createdAt).toLocaleString('pt-BR')}</span>
                          <span className="text-gray-400">·</span>
                          <span>{venda.operador}</span>
                          {venda.cancelada && (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                              Estornada
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`text-lg font-bold ${
                              venda.cancelada ? 'text-gray-400 line-through' : 'text-gray-900'
                            }`}
                          >
                            {moeda(Number(venda.valorTotal))}
                          </span>
                          {!venda.cancelada && podeEstornar && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Estornar venda"
                              onClick={() =>
                                setEstornando({
                                  id: venda.id,
                                  valorTotal: venda.valorTotal,
                                  createdAt: venda.createdAt,
                                  itens: venda.itens,
                                })
                              }
                            >
                              <Undo2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </div>

                      <ul className="mt-2 space-y-0.5 text-sm text-gray-600">
                        {venda.itens.map((item, i) => (
                          <li key={i} className="flex justify-between">
                            <span>
                              {item.quantidade}× {item.produtoNome}
                              <span className="ml-2 font-mono text-xs text-gray-400">
                                {item.codigo}
                              </span>
                            </span>
                            <span>{moeda(Number(item.subtotal))}</span>
                          </li>
                        ))}
                      </ul>

                      {venda.cancelada && venda.motivoCancelamento && (
                        <p className="mt-2 border-t border-red-200 pt-2 text-sm text-red-700">
                          <strong>Motivo do estorno:</strong> {venda.motivoCancelamento}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <Pagination meta={lista.meta} onPageChange={setPage} itemLabel="vendas" />
            </>
          )}
        </CardContent>
      </Card>

      <EstornarVendaModal venda={estornando} onClose={() => setEstornando(null)} />
    </div>
  );
}
